/** @format */

import { AccountSettings, RewardDeliverySettings } from '@my-account/data-access/interfaces';
import {
	Address,
	Chain,
	Environment,
	PersonalInformation,
	Phone
} from '@shared/data-access/interfaces';
import { HttpService, MyAccountAnalyticsService, Store } from '@shared/data-access/services';
import { MediaType, MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { Observable, of, throwError } from 'rxjs';
import { RewardDeliveryMethodEnum, SubscriptionFrequencyEnum } from '@my-account/data-access/enums';
import { catchError, map } from 'rxjs/operators';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class AccountSettingsService {
	apiVersion = '3';

	constructor(
		private chain: Chain,
		private environment: Environment,
		private httpService: HttpService,
		private store: Store,
		private myAccountAnalyticsService: MyAccountAnalyticsService
	) {}

	public getAccountSettings(): Observable<AccountSettings> {
		const url = `${this.environment.baseUrls.athleteService}/v${this.apiVersion}/accountsettings/?chain=${this.chain.chainIdentifierAbbr}`;

		const httpOptions = {
			headers: new HttpHeaders().set('X-TIMEOUT', DCSG_CONSTANTS.timeouts.long.toString()),
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.get<AccountSettings>(url, 0, httpOptions).pipe(
			map((result: AccountSettings) => {
				this.store.set('ACCOUNT_SETTINGS', result);
				return result;
			}),
			catchError((err) => {
				this.myAccountAnalyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_ACCOUNT_SETTINGS,
					'Failed to get account settings.',
					err
				);
				const errorSettings: AccountSettings = {
					rewardDeliverySettings: undefined,
					subscriptionSettings: undefined,
					addresses: [],
					couldNotRetrieveSettings: false
				};
				errorSettings.couldNotRetrieveSettings = true;
				this.store.set('ACCOUNT_SETTINGS', errorSettings);
				return of(errorSettings);
			})
		);
	}

	public getOptedInStatus(
		phoneNumber: string
	): Observable<{ list: string; optStatus: string; phoneNumber: string }> {
		const url = `${this.environment.apiTokens.exComm.baseUrl}/sms/subscription/${phoneNumber}/dsg_transactional`;

		const httpOptions = {
			headers: new HttpHeaders().set(
				'Ocp-Apim-Subscription-Key',
				this.environment.apiTokens.exComm.apimSecret
			),
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService
			.get<{ list: string; optStatus: string; phoneNumber: string }>(url, 0, httpOptions)
			.pipe(
				map((result) => result),
				catchError((err) => {
					this.myAccountAnalyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_ACCOUNT_SETTINGS,
						'Failed to get account opted in status.',
						err
					);
					return throwError(err);
				})
			);
	}

	public optOutOrderSubscription(phoneNumber: string) {
		const url = `${this.environment.apiTokens.exComm.baseUrl}/sms/subscription/${phoneNumber}/dsg_transactional`;

		const httpOptions = {
			headers: new HttpHeaders().set(
				'Ocp-Apim-Subscription-Key',
				this.environment.apiTokens.exComm.apimSecret
			),
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.delete<string>(url, httpOptions).pipe(
			map((result) => result),
			catchError((err) => {
				this.myAccountAnalyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_ACCOUNT_SETTINGS,
					'Failed to get account opted in status.',
					err
				);
				return throwError(err);
			})
		);
	}

	public optInOrderSubscription<T>(phoneNumber: string) {
		const url = `${this.environment.apiTokens.exComm.baseUrl}/sms/subscription`;

		const httpOptions = {
			headers: new HttpHeaders().set(
				'Ocp-Apim-Subscription-Key',
				this.environment.apiTokens.exComm.apimSecret
			),
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService
			.post<T>(
				url,
				{ phoneNumber: phoneNumber, list: 'dsg_transactional', signupSource: 'loyalty' },
				httpOptions
			)
			.pipe(
				map((result) => result),
				catchError((err) => {
					this.myAccountAnalyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_ACCOUNT_SETTINGS,
						'Failed to get account opted in status.',
						err
					);
					return throwError(err);
				})
			);
	}

	public updateRewardDeliveryMethod(
		rewardDeliveryMethod: RewardDeliveryMethodEnum
	): Observable<boolean> {
		const url = `${this.environment.baseUrls.athleteService}/v${this.chain.apiVersion}/accountsettings/updaterewarddeliverymethod?chain=${this.chain.chainIdentifierAbbr}`;

		const model: RewardDeliverySettings = {
			rewardDeliveryMethod
		};

		const httpOptions = {
			headers: {
				'Content-Type': MediaType.APPLICATION_JSON
			},
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.post(url, model, httpOptions);
	}

	public updateSubscriptions(
		emailSubscription: SubscriptionFrequencyEnum,
		mailSubscription: SubscriptionFrequencyEnum,
		textSubscription: SubscriptionFrequencyEnum
	): Observable<boolean> {
		const url = `${this.environment.baseUrls.athleteService}/v${this.chain.apiVersion}/accountsettings/updatesubscriptions?chain=${this.chain.chainIdentifierAbbr}`;

		const model = {
			EmailSubscription: emailSubscription,
			MailSubscription: mailSubscription,
			TextSubscription: textSubscription
		};

		const httpOptions = {
			headers: {
				'Content-Type': MediaType.APPLICATION_JSON
			},
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.post(url, model, httpOptions);
	}

	public updateAddress(address: Address): Observable<boolean> {
		const url = `${this.environment.baseUrls.athleteService}/v2/accountsettings/updateaddress`;

		const httpOptions = {
			headers: {
				'Content-Type': MediaType.APPLICATION_JSON
			},
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.post(url, address, httpOptions).pipe(
			/* istanbul ignore next */
			map((result: boolean) => {
				/* istanbul ignore next */
				this.store.select<PersonalInformation>('PERSONAL_INFO').subscribe((info) => {
					const storeAddress = info.addresses.filter((x) => x.id === address.id)[0];
					storeAddress.addressName = address.addressName;
					storeAddress.addressLines = address.addressLines;
					storeAddress.city = address.city;
					storeAddress.state = address.state;
					storeAddress.zipCode = address.zipCode;
					storeAddress.country = address.country;
					this.store.set('PERSONAL_INFO', info);
				});
				/* istanbul ignore next */
				return result;
			}),
			catchError((err) => {
				this.myAccountAnalyticsService.generateLogError(
					MyAccountErrorLogKeys.MYACCOUNT_ACCOUNT_SETTINGS,
					'Failed to update address.',
					err
				);
				return of(false);
			})
		);
	}

	public updatePhone(phone: Phone): Observable<boolean> {
		const url = `${this.environment.baseUrls.athleteService}/v2/accountsettings/updatephone`;

		const httpOptions = {
			headers: {
				'Content-Type': MediaType.APPLICATION_JSON,
				responseType: MediaType.TEXT
			},
			withCredentials: true,
			useApiAuth: true
		};

		return this.httpService.post(url, phone, httpOptions);
	}

	public getPhone(): Observable<string> {
		const httpOptions = {
			headers: {
				'Content-Type': MediaType.APPLICATION_JSON,
				responseType: MediaType.TEXT
			},
			withCredentials: true,
			useApiAuth: true
		};
		const url = `${this.environment.baseUrls.athleteService}/v${this.chain.apiVersion}/accountsettings/phone?chain=${this.chain.chainIdentifierAbbr}`;

		return this.httpService.get(url, 1, httpOptions);
	}
}
